import type { IChecklistGenerationTargets } from "@/api/clause-checklists/checklist-generation.types";
import { ChecklistGroupField } from "@/components/admin/dialogs/clause-checklists/ChecklistGroupField";
import { ActionButton } from "@/components/shared/buttons/ActionButton";
import { OrganizationAutocomplete } from "@/components/shared/OrganizationAutocomplete";
import { Pill } from "@/components/shared/Pill";
import { TextField } from "@/components/shared/TextField";
import { Button } from "@/components/ui/button";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { Sparkles } from "lucide-react";
import { useId, useState } from "react";
import { DRAFT_GROUP_HELP } from "./DraftPublishTargets";

const NO_DESTINATION = "Choose an organization or a group before generating.";

interface GenerationPromptProps {
    onGenerate: (prompt: string, targets: IChecklistGenerationTargets) => void;
    disabled?: boolean;
    initialPrompt?: string;
    initialOrganizationId?: number;
    initialOrganizationGroupId?: number;
}

interface ExampleSuggestion {
    label: string;
    fullPrompt: string;
}

const EXAMPLE_SUGGESTIONS: ExampleSuggestion[] = [
    {
        label: "Design professionals — commercial property",
        fullPrompt:
            "Architect and engineering firms with annual revenue between $5M–$50M seeking commercial property coverage for their office premises, equipment, and business interruption. Clients operate in multi-state jurisdictions and require broad-form coverage with replacement cost valuation.",
    },
    {
        label: "Healthcare providers — cyber liability",
        fullPrompt:
            "Multi-site healthcare provider (clinics and outpatient facilities) handling protected health information under HIPAA. Requires cyber liability coverage including first-party breach response costs, regulatory defense, ransomware extortion, and third-party liability for patient data exposure.",
    },
    {
        label: "Construction contractors — general liability",
        fullPrompt:
            "General contracting firm performing commercial construction projects valued $500K–$10M. Requires CGL coverage with completed operations, products liability, and contractor's protective liability. Must accommodate subcontractor-heavy operations and projects in multiple states.",
    },
    {
        label: "Technology companies — professional liability",
        fullPrompt:
            "B2B SaaS company providing mission-critical software to financial services clients. Needs technology E&O / professional liability covering software failures, data loss, and service interruptions. Coverage should include network security liability, media liability, and contractual liability exposure.",
    },
];

export const GenerationPrompt = ({
    onGenerate,
    disabled = false,
    initialPrompt = "",
    initialOrganizationId,
    initialOrganizationGroupId,
}: GenerationPromptProps) => {
    const [prompt, setPrompt] = useState(initialPrompt);
    const [organizationId, setOrganizationId] = useState<number | null>(
        initialOrganizationId ?? null
    );
    const [groupId, setGroupId] = useState<number | null>(
        initialOrganizationGroupId ?? null
    );

    const destinationHintId = useId();
    const destinationMissing = organizationId === null && groupId === null;
    const isGenerateDisabled = disabled || prompt.trim().length < 10;

    const handleSuggestionClick = (suggestion: ExampleSuggestion) => {
        if (!disabled) {
            setPrompt(suggestion.fullPrompt);
        }
    };

    const handleGenerate = () => {
        if (!isGenerateDisabled && !destinationMissing) {
            onGenerate(prompt.trim(), {
                ...(organizationId !== null && {
                    organization_id: organizationId,
                }),
                ...(groupId !== null && { organization_group_id: groupId }),
            });
        }
    };

    return (
        <>
            <Paper
                variant="outlined"
                sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}
            >
                <Stack spacing={2.5}>
                    <Stack spacing={2} minWidth={0}>
                        <Stack
                            direction="row"
                            flexWrap="wrap"
                            useFlexGap
                            gap={1}
                            alignItems="flex-start"
                            minWidth={0}
                        >
                            <Box sx={{ flex: "1 1 14rem", minWidth: 0 }}>
                                <OrganizationAutocomplete
                                    value={organizationId}
                                    onChange={setOrganizationId}
                                    disabled={disabled}
                                />
                            </Box>
                            {organizationId !== null && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    disabled={disabled}
                                    onClick={() => setOrganizationId(null)}
                                >
                                    Remove organization
                                </Button>
                            )}
                        </Stack>
                        <ChecklistGroupField
                            value={groupId}
                            onChange={setGroupId}
                            disabled={disabled}
                            help={DRAFT_GROUP_HELP}
                        />
                        {destinationMissing && (
                            <Typography
                                id={destinationHintId}
                                variant="body2"
                                color="text.secondary"
                            >
                                {NO_DESTINATION}
                            </Typography>
                        )}
                    </Stack>

                    <Stack>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Describe the brokers scenario
                        </Typography>

                        {/* Textarea */}
                        <TextField
                            multiline
                            minRows={4}
                            maxRows={10}
                            fullWidth
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            disabled={disabled}
                            placeholder={
                                "Include the industry, business size, and type of coverage needed. The more context you provide, the more tailored the checklist will be..." +
                                "\n" +
                                "\n" +
                                "e.g. Mid-size logistics company operating a fleet of 40+ trucks across the southeast, seeking commercial auto and cargo liability coverage. They have had two at-fault claims in the past three years and want to understand what endorsements are typically required..."
                            }
                            disableFocusShadow={false}
                            slotProps={{
                                htmlInput: {
                                    "aria-label":
                                        "Describe the broker's scenario",
                                },
                            }}
                        />
                    </Stack>

                    {/* Example suggestions */}
                    <Stack spacing={1}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                fontWeight: 500,
                            }}
                        >
                            Try an example
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.75,
                            }}
                        >
                            {EXAMPLE_SUGGESTIONS.map((suggestion) => (
                                <Pill
                                    key={suggestion.label}
                                    label={suggestion.label}
                                    clickable={!disabled}
                                    onClick={() =>
                                        handleSuggestionClick(suggestion)
                                    }
                                    disabled={disabled}
                                    sx={{
                                        fontSize: 12,
                                        height: 24,
                                    }}
                                />
                            ))}
                        </Box>
                    </Stack>
                </Stack>
            </Paper>

            {/* Generate CTA */}
            <Stack sx={{ alignItems: "flex-end" }}>
                <ActionButton
                    variant="contained"
                    disableElevation
                    disabled={isGenerateDisabled}
                    aria-disabled={destinationMissing || undefined}
                    aria-describedby={
                        destinationMissing ? destinationHintId : undefined
                    }
                    onClick={handleGenerate}
                    startIcon={<Sparkles size={16} />}
                >
                    Generate Checklist
                </ActionButton>
            </Stack>
        </>
    );
};
